import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControl, Grid, Link, RadioGroup, TextField, Typography } from '@mui/material';

import { LevelRadioButton, Loading } from 'components';
import { ROUTES } from 'consts';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices';
import { useSuspendServiceFormManager } from './suspend-service-form.hook';

interface SuspendServiceFormProps {
  zoneId: number;
  onInit?: (loading: boolean) => void;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
  supervisor?: boolean;
}

export const SuspendServiceForm = React.forwardRef(
  ({ zoneId, onInit = () => {}, onSubmitting = () => {}, next, supervisor = false }: SuspendServiceFormProps, ref) => {
    const [{ formManager, loading_form, loading_submit }] = useSuspendServiceFormManager({
      zoneId,
      next,
    });

    const { orgSlug } = useAppSelector(selectCurrentUserInfo);
    const navigate = useNavigate();

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onInit(loading_form), [loading_form]);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    return loading_form ? (
      <Loading text="Loading schedule..." height={320} />
    ) : (
      <Box sx={{ paddingY: 2 }}>
        <form onSubmit={formManager.handleSubmit} autoComplete="off">
          <Grid container sx={{ alignItems: 'center' }} spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <RadioGroup row name="payment_method" value={formManager.values.impactsBuilding ? 'building' : 'zone'}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <LevelRadioButton
                        value={'zone'}
                        active={!formManager.values.impactsBuilding}
                        title={'Zone Level'}
                        subtitle={formManager.values.zoneName}
                        description={
                          'Suspending the zone will temporarily turn off the scheduled service(s) for the remainder of the day.'
                        }
                        onClick={(value) => formManager.setFieldValue('impactsBuilding', value === 'building')}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <LevelRadioButton
                        value={'building'}
                        active={formManager.values.impactsBuilding}
                        title={'Building Level'}
                        subtitle={formManager.values.buildingName}
                        description={
                          supervisor ? (
                            'Suspending all zones will temporarily turn off the scheduled service(s) for the remainder of the day.'
                          ) : (
                            <Typography color="textSecondary" sx={{ fontSize: 12, marginTop: 0.5 }}>
                              Suspending{' '}
                              <Link
                                component="button"
                                variant="body2"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() => navigate(`/${orgSlug}/${ROUTES.ZONES}/${ROUTES.ZONES_ACTIVE}`)}
                                sx={{ lineHeight: 'inherit', fontWeight: 600 }}
                              >
                                all zones
                              </Link>{' '}
                              will temporarily turn off the scheduled service(s) for the remainder of the day.
                            </Typography>
                          )
                        }
                        onClick={(value) => formManager.setFieldValue('impactsBuilding', value === 'building')}
                      />
                    </Grid>
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Please add a comment why.
              </Typography>

              <Typography color="textSecondary" sx={{ fontSize: 12, marginY: 1 }}>
                All comments will be added to the Service Zone report.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comment"
                placeholder="Add comment..."
                variant="outlined"
                value={formManager.values.comment}
                onChange={formManager.handleChange('comment')}
                error={!!(formManager.errors.comment && formManager.touched.comment)}
                helperText={(formManager.touched.comment ? formManager.errors.comment : '') as string}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  },
);
